// Every company logo URL the GUI renders is built here, and nowhere else.
//
// Two origins exist. `legacy` is the FactSet-fed CloudFront bucket at
// logo.ortex.com/{sid}r.png that the platform has always used. `edge` is
// the ORTEX logo service (company-and-people-logos), addressed by the same
// security id and carrying a one-hour token that web mints for us. Which one
// is live is decided by web - GET /interface/api/security/logos/config -
// from its LOGOS_SOURCE environment variable, so rolling back to the FactSet
// logos is a config flip on web with no GUI release: every client picks the
// new answer up within one refresh interval, and until the first answer
// arrives (or if it never does) everything renders from `legacy`.
import axios from "axios";
import { useEffect, useState } from "react";

export type LogoSource = "legacy" | "edge";
/** How the token reaches the edge: in the URL (`?t=`), or as the `logos_t` cookie web set on the site. */
export type TokenTransport = "query" | "cookie";
export type LogoVariant = "rect" | "square";
/** The page background a logo is being drawn on. */
export type LogoTheme = "light" | "dark";

export interface LogosConfig {
    source: LogoSource;
    baseUrl: string;
    token: string | null;
    /** Unix seconds; null on legacy. */
    expiresAt: number | null;
    /** `cookie` means the URL can omit the token, so the browser can cache the image across rotations. */
    transport: TokenTransport;
    /**
     * The artwork generation, from web. It rides on every logo URL as `e=`, so that when the
     * bytes behind an unchanged URL change - a corpus-wide re-measurement of which background
     * each logo needs, say - bumping it on the server makes every browser refetch once instead
     * of showing what it cached for the week the edge allows.
     */
    assetEpoch: string;
}

export const LEGACY_LOGO_BASE = "https://logo.ortex.com";
export const CONFIG_PATH = "/interface/api/security/logos/config";
export const BLANK_GIF = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const DEFAULT_CONFIG: LogosConfig = { source: "legacy", baseUrl: LEGACY_LOGO_BASE, token: null, expiresAt: null, transport: "query", assetEpoch: "" };
// Tokens live 60 minutes and web hands out one that is at most 40 minutes
// old, so refreshing every 15 keeps a page at least 5 minutes clear of expiry.
const REFRESH_MS = 15 * 60 * 1000;
const RETRY_MS = 60 * 1000;

let current: LogosConfig = DEFAULT_CONFIG;
// The page theme, set by _app: edge URLs carry it as ?theme= so the service answers with
// artwork that reads on that background (recoloured, or on its own plate) - which is what
// makes the blanket grey plate unnecessary on the edge source.
let pageTheme: "light" | "dark" | null = null;
let fetchedAt = 0;
// Until web has answered once, nobody knows which origin to use. Rendering `legacy` in the
// meantime made every page fetch each logo twice (legacy on the first paint, edge after the
// flip), so before the first answer a logo is a blank pixel. Never true on the server, which
// keeps SSR markup and the first client render identical.
let resolved = false;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify() {
    listeners.forEach((l) => l());
}

function fromResponse(body: any): LogosConfig {
    // web's renderer camelCases every key (baseUrl, expiresAt); accept snake_case too so a
    // change on either side cannot silently drop the base URL and turn every logo into a
    // relative request against app.ortex.com - which is exactly what happened on 2026-09-05.
    if (!body || body.source !== "edge" || !body.token) return DEFAULT_CONFIG;
    const baseUrl = String(body.baseUrl ?? body.base_url ?? "").replace(/\/+$/, "");
    if (!/^https:\/\//.test(baseUrl)) return DEFAULT_CONFIG;
    const expiresAt = body.expiresAt ?? body.expires_at;
    return {
        source: "edge",
        baseUrl,
        token: String(body.token),
        expiresAt: typeof expiresAt === "number" ? expiresAt : null,
        transport: body.transport === "cookie" ? "cookie" : "query",
        assetEpoch: String(body.assetEpoch ?? body.asset_epoch ?? ""),
    };
}

/** Fetch (or re-fetch) the config from web. Safe to call often; coalesces and rate-limits itself. */
export function loadLogosConfig(force = false): Promise<void> {
    const now = Date.now();
    const stale = now - fetchedAt > (current.source === "edge" ? REFRESH_MS : RETRY_MS);
    if (inflight) return inflight;
    if (!force && !stale) return Promise.resolve();
    inflight = axios
        .get(CONFIG_PATH)
        .then((res) => {
            const next = fromResponse(res.status === 200 ? res.data : null);
            fetchedAt = Date.now();
            const first = !resolved;
            resolved = true;
            if (first || next.source !== current.source || next.token !== current.token || next.baseUrl !== current.baseUrl || next.transport !== current.transport) {
                current = next;
                notify();
            }
        })
        .catch(() => {
            // Leave whatever we had; `legacy` needs no token and always works.
            fetchedAt = Date.now();
            if (!resolved) {
                resolved = true;
                notify();
            }
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

/** The live config. Subscribing components re-render when web flips the source or rotates the token. */
export function useLogosConfig(): LogosConfig {
    const [cfg, setCfg] = useState<LogosConfig>(current);
    useEffect(() => {
        const listener = () => setCfg(current);
        listeners.add(listener);
        setCfg(current);
        void loadLogosConfig();
        const timer = setInterval(() => void loadLogosConfig(), REFRESH_MS);
        return () => {
            listeners.delete(listener);
            clearInterval(timer);
        };
    }, []);
    return cfg;
}

/**
 * Whether the token cookie web set on `.ortex.com` reaches the edge from this page: only in a
 * browser, and only on an ortex.com host (a preview or local checkout cannot carry it). Then the
 * image URL carries no token, so it is identical for every visitor and every hour and the browser
 * keeps the bytes for the 7 days the edge allows instead of refetching after each rotation.
 */
export function tokenInCookie(cfg: LogosConfig): boolean {
    if (cfg.transport !== "cookie" || typeof window === "undefined") return false;
    return /(^|\.)ortex\.com$/i.test(window.location.hostname);
}

/** The credential part of an edge URL: `t=` unless the cookie carries it. */
function credentialParams(cfg: LogosConfig): URLSearchParams {
    const params = new URLSearchParams();
    if (cfg.token && !tokenInCookie(cfg)) params.set("t", cfg.token);
    return params;
}

/** The page theme, for callers that need to know what background they are drawing on. */
export function currentLogosTheme(): LogoTheme | null {
    return pageTheme;
}

/** Tell the logo helpers which theme the page is on; subscribers re-render so URLs pick it up. */
export function setLogosTheme(theme: "light" | "dark" | null | undefined): void {
    const next = theme === "dark" || theme === "light" ? theme : null;
    if (next === pageTheme) return;
    pageTheme = next;
    notify();
}

/** The config as last fetched, for code that is not a React component (treemap HTML, event handlers). */
export function currentLogosConfig(): LogosConfig {
    return current;
}

/** Whether web has answered at least once; before that `logoUrl` renders nothing. */
export function logosConfigResolved(): boolean {
    return resolved;
}

export interface LogoUrlOptions {
    /** Cache-buster after a retry; 0 / undefined adds nothing so the normal URL stays byte-identical. */
    retry?: number;
    /** Ask the edge for artwork that reads on this background. Ignored on legacy. */
    theme?: "light" | "dark";
    /** Override the config (server-side callers pass what they fetched). */
    config?: LogosConfig;
    /**
     * Require artwork that reads on `theme` rather than merely preferring it. The edge answers
     * `406 needs_background` (an `onError` in the browser) when the logo would be illegible, which
     * is how `useCompanyLogo` learns to draw a plate. Ignored without a theme, and on legacy.
     */
    strict?: boolean;
}

/** What a row knows about the company; the best public identifier wins. */
export interface LogoSubject {
    isin?: string | null;
    ortexSid?: number | string | null;
    ortex_sid?: number | string | null;
    /** Ticker plus the exchange symbol the platform shows (`SAP` + `XETRA`): the last public identifier tried. */
    ticker?: string | null;
    exchangeSymbol?: string | null;
    exchange_symbol?: string | null;
    exchangesymbol?: string | null;
    exchange?: string | null;
    /** The legacy id (web's security id, which is S&P Capital IQ's). Only the legacy origin ever sees it. */
    securityId?: number | string | null;
    security_id?: number | string | null;
}

/**
 * Exchange symbols the platform uses for US venues. A US ticker is unique across all of them, so
 * the logo service is asked for `ticker:X:US` rather than a specific venue - which also sidesteps
 * Nasdaq's Global Select / Capital Market segment MICs.
 */
const US_EXCHANGES = new Set(["NASDAQ", "NYSE", "ARCA", "AMEX", "NYSEAM", "NYSEARCA", "BATS", "CBOE", "IEX", "NASDAQGS", "NASDAQGM", "NASDAQCM"]);

/**
 * Every public identifier a row holds for the company, most specific first: ISIN, ORTEX's own
 * id, `ticker:EXCHANGE`. Never the CIQ security id - that is a vendor's id and must not appear in
 * a public URL (the legacy origin, which is keyed by it, is handled separately in `logoUrl`).
 */
export function logoIdentifiers(subject: LogoSubject | number | string | null | undefined): { scheme: string; value: string }[] {
    if (subject === null || subject === undefined || typeof subject !== "object") return [];
    const out: { scheme: string; value: string }[] = [];
    const isin = String(subject.isin || "").trim();
    if (/^[A-Z]{2}[A-Z0-9]{9}[0-9]$/i.test(isin)) out.push({ scheme: "isin", value: isin.toUpperCase() });
    const ortexSid = subject.ortexSid ?? subject.ortex_sid;
    if (ortexSid) out.push({ scheme: "ortex_sid", value: String(ortexSid) });
    const ticker = String(subject.ticker || "").trim().toUpperCase();
    const rawExchange = String(subject.exchangeSymbol || subject.exchange_symbol || subject.exchangesymbol || subject.exchange || "").trim().toUpperCase();
    const exchange = US_EXCHANGES.has(rawExchange) ? "US" : rawExchange;
    if (/^[A-Z0-9][A-Z0-9.\-]{0,15}$/.test(ticker) && /^[A-Z0-9]{2,12}$/.test(exchange)) out.push({ scheme: "ticker", value: `${ticker}:${exchange}` });
    return out;
}

/** The single best identifier (first of `logoIdentifiers`), for callers that want one. */
export function logoIdentifier(subject: LogoSubject | number | string | null | undefined): { scheme: string; value: string } | null {
    return logoIdentifiers(subject)[0] ?? null;
}

/** The `{scheme}/{value}` path segment: one identifier as is, several as `any/a:1,b:2` so the edge tries each. */
function identifierPath(ids: { scheme: string; value: string }[]): string {
    if (ids.length === 1) return `${ids[0].scheme}/${encodeURIComponent(ids[0].value)}`;
    return `any/${encodeURIComponent(ids.map((i) => `${i.scheme}:${i.value}`).join(","))}`;
}

/**
 * The URL for a company's logo, or null when the row identifies no company.
 *
 * `subject` is a row (ISIN / ORTEX id / legacy id) or, for older call sites, the legacy id
 * itself. The legacy origin only knows the legacy id, so a row without one renders nothing there.
 */
export function logoUrl(subject: LogoSubject | number | string | null | undefined, variant: LogoVariant = "rect", opts: LogoUrlOptions = {}): string | null {
    const ids = logoIdentifiers(subject);
    if (ids.length === 0) return null;
    // No answer from web yet (or server-side without an explicit config): blank, not legacy.
    if (!opts.config && !resolved) return null;
    const cfg = opts.config ?? current;
    const retry = opts.retry ? String(opts.retry) : "";
    if (cfg.source === "edge" && cfg.token) {
        const params = credentialParams(cfg);
        const theme = opts.theme ?? pageTheme;
        if (theme) params.set("theme", theme);
        if (theme && opts.strict) params.set("strict", "1");
        if (cfg.assetEpoch) params.set("e", cfg.assetEpoch);
        if (retry) params.set("v", retry);
        const qs = params.toString();
        return `${cfg.baseUrl}/v1/company/${identifierPath(ids)}/${variant}.png${qs ? `?${qs}` : ""}`;
    }
    const legacySid = typeof subject === "object" && subject ? subject.securityId ?? subject.security_id : subject;
    if (!legacySid) return null;
    const suffix = variant === "square" ? "s" : "r";
    return `${LEGACY_LOGO_BASE}/${legacySid}${suffix}.png${retry ? `?v=${retry}` : ""}`;
}

/** `logoUrl`, falling back to a transparent pixel so an `<img>` always has a src. */
export function logoSrc(subject: LogoSubject | number | string | null | undefined, variant: LogoVariant = "rect", opts: LogoUrlOptions = {}): string {
    return logoUrl(subject, variant, opts) ?? BLANK_GIF;
}

/**
 * Server-side twin of `loadLogosConfig` for the edge runtime (OG cards): no axios, no store.
 * Falls back to legacy on any failure, exactly as the browser does.
 */
export async function fetchLogosConfig(apiBase: string, timeoutMs = 800): Promise<LogosConfig> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        const res = await fetch(`${apiBase.replace(/\/+$/, "")}${CONFIG_PATH}`, { signal: ctrl.signal, cache: "no-store" });
        if (!res.ok) return DEFAULT_CONFIG;
        return fromResponse(await res.json());
    } catch {
        return DEFAULT_CONFIG;
    } finally {
        clearTimeout(timer);
    }
}

/** What an API row knows about a person: the logo service's key, and the legacy photo URL. */
export interface PortraitSubject {
    /** `{scheme}/{value}` from web - `bioguide/P000197`, `cik/0000004962`, `person/executive:jane-doe`. */
    portrait?: string | null;
    filerPortrait?: string | null;
    /** The legacy CDN photo, shown while `legacy` is the source (or when a row has no key). */
    photo?: string | null;
    filerPhoto?: string | null;
}

export type PortraitVariant = "portrait" | "square";

/** The URL for a person's portrait, or null when there is nothing to show (yet). */
export function portraitUrl(subject: PortraitSubject | null | undefined, variant: PortraitVariant = "portrait", opts: LogoUrlOptions = {}): string | null {
    if (!subject) return null;
    if (!opts.config && !resolved) return null;
    const cfg = opts.config ?? current;
    const key = (subject.portrait ?? subject.filerPortrait ?? "").replace(/^\/+/, "");
    const slash = key.indexOf("/");
    if (cfg.source === "edge" && cfg.token && slash > 0) {
        const params = credentialParams(cfg);
        const theme = opts.theme ?? pageTheme;
        if (theme) params.set("theme", theme);
        if (theme && opts.strict) params.set("strict", "1");
        if (cfg.assetEpoch) params.set("e", cfg.assetEpoch);
        if (opts.retry) params.set("v", String(opts.retry));
        const scheme = key.slice(0, slash);
        const value = key.slice(slash + 1);
        const qs = params.toString();
        return `${cfg.baseUrl}/v1/person/${scheme}/${encodeURIComponent(value)}/${variant}.jpg${qs ? `?${qs}` : ""}`;
    }
    return subject.photo || subject.filerPhoto || null;
}

/** `portraitUrl` with a stand-in: a blank pixel until web has answered, then `fallback`. */
export function portraitSrc(subject: PortraitSubject | null | undefined, variant: PortraitVariant = "portrait", fallback: string = BLANK_GIF, opts: LogoUrlOptions = {}): string {
    if (!opts.config && !resolved) return BLANK_GIF;
    return portraitUrl(subject, variant, opts) ?? fallback;
}

/**
 * The plate a logo is drawn on when the service says its artwork cannot read on the page: the
 * background of the *other* theme, the one the artwork was drawn for, so a navy wordmark on a
 * dark page sits on white exactly as it does on the company's own site, and a white wordmark on
 * a light page sits on dark. Before 2026-10-09 the plate was white whichever theme had failed,
 * which on light pages put dark-page artwork (white marks) on a white plate: invisible.
 */
export const LOGO_PLATE_COLORS: Record<LogoTheme, string> = { light: "#ffffff", dark: "#121212" };
/** The plate for artwork drawn for light pages (kept for callers that predate the per-theme map). */
export const LOGO_PLATE_COLOR = LOGO_PLATE_COLORS.light;

/** The plate to draw under artwork made for `artworkTheme` when it is shown on the other theme. */
export function plateFor(artworkTheme: LogoTheme): string {
    return LOGO_PLATE_COLORS[artworkTheme];
}

/** What `useCompanyLogo` gives a component: the URL to draw, the plate to draw it on, and the miss handler. */
export interface CompanyLogo {
    /** `<img src>`; `BLANK_GIF` while the config is unresolved or the row identifies no company. */
    src: string;
    /** CSS background for the wrapper, or `null` when the artwork needs none. */
    plate: string | null;
    /** Pass to `onError`. */
    onError: () => void;
    /** True once every attempt has failed: there is no logo, so render the fallback. */
    failed: boolean;
}

/**
 * A company logo that adapts to the page background.
 *
 * On a themed page the first request is strict (`?theme=dark&strict=1`): the edge serves the
 * artwork only if it reads on that background, and answers `406` otherwise. The `<img>` cannot
 * read a header or a status, but it does fire `onError`, and that is the signal: the hook then
 * asks for the artwork drawn for the *other* background and draws it on a plate of that colour.
 * A second failure means there is genuinely no logo, and `failed` tells the caller to fall back.
 *
 * On the legacy origin, and on an unthemed page, nothing is strict and the plate is the platform's
 * historical grey-on-dark behaviour, decided by the caller as before.
 */
export function useCompanyLogo(
    subject: LogoSubject | number | string | null | undefined,
    variant: LogoVariant = "rect",
    opts: LogoUrlOptions & {
        /** Called once, when every attempt has failed: the company genuinely has no logo here. */
        onMissing?: () => void;
    } = {},
): CompanyLogo {
    const cfg = useLogosConfig();
    const theme = opts.theme ?? currentLogosTheme();
    const strictable = cfg.source === "edge" && !!theme;
    const [stage, setStage] = useState<0 | 1 | 2>(0);
    const key = `${logoIdentifiers(subject)
        .map((i) => `${i.scheme}:${i.value}`)
        .join(",")}|${variant}|${theme ?? ""}|${cfg.source}`;
    const [seen, setSeen] = useState(key);
    // A row can be recycled (virtualised grids, a new search result in the same slot): start over.
    if (seen !== key) {
        setSeen(key);
        setStage(0);
    }
    const plated = strictable && stage === 1;
    // On a plate, ask for the artwork made for the other theme and draw it on that theme's background.
    const artworkTheme: LogoTheme | undefined = plated ? (theme === "dark" ? "light" : "dark") : theme ?? undefined;
    const url = logoUrl(subject, variant, {
        ...opts,
        theme: artworkTheme,
        strict: strictable && stage === 0,
    });
    return {
        src: url ?? BLANK_GIF,
        plate: plated && artworkTheme ? plateFor(artworkTheme) : null,
        failed: stage === 2 || (stage === 1 && !strictable),
        onError: () => {
            const next = strictable && stage === 0 ? 1 : 2;
            if (next === 2) opts.onMissing?.();
            setStage(next);
        },
    };
}
